import { PRICING_PLANS } from "../../../../src/shared/constants";
import CollapsableContainer from "../../components/CollapsableContainer";
import { SudoButton } from "../../sudokit/components/SudoButton";

export const ManagePlans = ({ onToolButtonClick, toolsEnabled, subscription }) => {
  // Helper function to handle button clicks since SudoButton doesn't have built-in URL/params handling
  const handleButtonClick = (url, params) => {
    onToolButtonClick(url, params);
  };

  return (
    <CollapsableContainer
      title="Manage Plans"
      defaultCollapse={true}
    >
      <div className="space-y-8">
        {/* Set to Plan Universe Section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Set to Plan Universe</h3>
          <p className="text-sm text-gray-600 mb-4">Sets to user to be offered plans in the selected universe. Note: this does not convert their current plan, if they have one.</p>
          <div className="flex flex-wrap gap-2">
            {["1.0", "2.0", "3.1", "4.0"].map((version) => {
              return (
                <SudoButton
                  key={version}
                  onClick={() =>
                    handleButtonClick(`/api/subscriptions/${subscription.id}`, {
                      data: {
                        version,
                      },
                    })
                  }
                  disabled={!toolsEnabled}
                  variant="secondary"
                  size="small"
                >
                  {version}
                </SudoButton>
              );
            })}
          </div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Cancel Subscription and Reset User to Plan Universe</h3>
          <p className="text-sm text-gray-600 mb-4">This is mostly equivalent to resetting the user as if they were on a trial</p>
          <div className="flex flex-wrap gap-2">
            {["1.0", "2.0", "3.1", "4.0"].map((version) => {
              return (
                <SudoButton
                  key={version}
                  onClick={() =>
                    handleButtonClick(`/api/subscriptions/${subscription.id}/resetToVersion`, {
                      version,
                    })
                  }
                  disabled={!toolsEnabled}
                  variant="tertiary"
                  size="small"
                >
                  {version}
                </SudoButton>
              );
            })}
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Migrate User to 4.0</h3>
          <p className="text-sm text-gray-600 mb-4">
            This will run the migration script for this user according to this{" "}
            <a
              href="https://www.notion.so/sudowrite/Pricing-4-0-Credits-e729a1d22c4f4ea1811b90c223dfc5a8?pvs=4#5824c92f0db84cbabbfb73572f73e7f3"
              className="text-blue-600 hover:text-blue-800 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              migration strategy
            </a>
            . Make sure they are on a v2 or v3 plan first.
          </p>
          <SudoButton
            onClick={() => handleButtonClick(`/api/subscriptions/${subscription.id}/migrateToV4`, {})}
            disabled={!toolsEnabled}
            variant="primary"
            size="default"
          >
            Migrate to V4
          </SudoButton>
        </div>
        <div className="bg-red-50 p-6 rounded-lg border border-red-200">
          <h3 className="text-lg font-semibold text-red-900 mb-3">Force User into a Plan</h3>
          <div className="bg-red-100 p-4 rounded-md border border-red-300 mb-4">
            <p className="text-sm font-medium text-red-800">⚠️ WARNING: Forces the account into the version and plan, unsyncing it from Stripe. Use this ONLY for testing purposes.</p>
          </div>

          <div className="space-y-6">
            {Object.keys(PRICING_PLANS).map((version, i) => {
              return (
                <div
                  key={i}
                  className="bg-white p-4 rounded-md border border-gray-200"
                >
                  <h4 className="text-base font-medium text-gray-900 mb-3">Version {version}</h4>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <SudoButton
                      onClick={() =>
                        handleButtonClick(`/api/subscriptions/${subscription.id}`, {
                          data: {
                            version: version,
                            stripePlanId: null,
                            currentPeriodEnd: null,
                            trialPeriodEnd: new Date(),
                          },
                        })
                      }
                      disabled={!toolsEnabled}
                      variant="secondary"
                      size="small"
                    >
                      Trial
                    </SudoButton>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {PRICING_PLANS[version].plans.map((plan) => {
                      return (
                        <SudoButton
                          key={plan.id}
                          onClick={() =>
                            handleButtonClick(`/api/subscriptions/${subscription.id}`, {
                              data: {
                                version: version,
                                stripePlanId: plan.id,
                                trialPeriodEnd: new Date(),
                                stripeStatus: "active",
                                currentPeriodEnd: new Date("2200-01-01"),
                              },
                            })
                          }
                          disabled={!toolsEnabled}
                          variant="secondary"
                          size="small"
                        >
                          {plan.title} ${plan.price / 100} ({plan.interval}ly)
                        </SudoButton>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Version Mismatch Section */}
            <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
              <h4 className="text-base font-medium text-yellow-900 mb-3">Version Mismatch</h4>
              <SudoButton
                onClick={() =>
                  handleButtonClick(`/api/subscriptions/${subscription.id}`, {
                    data: {
                      version: "3.0",
                      stripePlanId: PRICING_PLANS["2.1"].plans[0].id,
                      trialPeriodEnd: new Date(),
                      stripeStatus: "active",
                      currentPeriodEnd: new Date("2200-01-01"),
                    },
                  })
                }
                disabled={!toolsEnabled}
                variant="tertiary"
                size="small"
                className="!text-yellow-700 hover:!text-yellow-800"
              >
                Set to v2 member AND offer v3 plans
              </SudoButton>
            </div>
          </div>
        </div>
      </div>
    </CollapsableContainer>
  );
};
